import React, { useRef, useState } from 'react';

import { Search, X } from 'lucide-react';

import { useStore } from '../../pages/workspace/hooks.ts';
import { green01, green03, greyP4, greyP5 } from '../../theme.ts';
import { ModelLayer } from '../../types/model.ts';
import { ToggleButton } from '../project-settings/components/ToggleButton';

interface AutoPositionSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  modelLayers: ModelLayer[];
  onUpdateLayer: (uuid: string, updates: Partial<ModelLayer>) => void;
}

const AutoPositionSidebar: React.FC<AutoPositionSidebarProps> = ({ isOpen, onClose, modelLayers, onUpdateLayer }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showWarning, setShowWarning] = useState(false);
  const popupRef = useRef<HTMLDivElement>(null);

  const {
    autoPositionLights,
    toggleLayerLuminaireHolder,
    desiredLuminaireHeight,
    setDesiredLuminaireHeight,
    setHoveredLayer,
  } = useStore();
  const MIN_HEIGHT = 2;
  const MAX_HEIGHT = 25;
  const [luminaireHeight, setLuminaireHeight] = useState(desiredLuminaireHeight.toString());

  const filteredLayers = modelLayers
    .filter((layer) => layer.originalName.toLowerCase().includes(searchTerm.toLowerCase()))
    .sort((a, b) => a.originalName.localeCompare(b.originalName));

  const toggleCanHoldLuminaire = (uuid: string) => {
    const layer = modelLayers.find((l) => l.uuid === uuid);
    if (layer) {
      toggleLayerLuminaireHolder(layer.threeJsName);
      onUpdateLayer(uuid, { canHoldLuminaire: !layer.canHoldLuminaire });
    }
    // Hide warning when user makes changes
    if (showWarning) {
      setShowWarning(false);
    }
  };

  const handleStartAutoPositioning = () => {
    // Check if at least one layer has canHoldLuminaire set to true
    const hasCanHoldLuminaire = modelLayers.some((layer) => layer.canHoldLuminaire);

    if (!hasCanHoldLuminaire) {
      setShowWarning(true);
      return;
    }

    const height = parseFloat(luminaireHeight);

    if (!isNaN(height) && height < MIN_HEIGHT) {
      setLuminaireHeight(MIN_HEIGHT.toString());
      setDesiredLuminaireHeight(MIN_HEIGHT);
    } else if (!isNaN(height) && height > MAX_HEIGHT) {
      setLuminaireHeight(MAX_HEIGHT.toString());
      setDesiredLuminaireHeight(MAX_HEIGHT);
    } else if (!isNaN(height)) {
      setDesiredLuminaireHeight(height);
    }

    autoPositionLights();
    onClose();
  };

  const handleLuminaireHeightChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const height = parseFloat(value);

    setLuminaireHeight(value);

    if (!isNaN(height) && height >= MIN_HEIGHT && height <= MAX_HEIGHT) {
      setDesiredLuminaireHeight(height);
      // Update all layers to the new default value
      modelLayers.forEach((layer) => {
        if (layer.canHoldLuminaire) {
          onUpdateLayer(layer.uuid, { desiredLuminaireHeight: height });
        }
      });
    }
  };

  const handleLayerHeightChange = (uuid: string, height: number) => {
    if (!isNaN(height) && height >= MIN_HEIGHT && height <= MAX_HEIGHT) {
      onUpdateLayer(uuid, { desiredLuminaireHeight: height });
    } else if (!isNaN(height) && height < MIN_HEIGHT) {
      onUpdateLayer(uuid, { desiredLuminaireHeight: MIN_HEIGHT });
    } else if (!isNaN(height) && height > MAX_HEIGHT) {
      onUpdateLayer(uuid, { desiredLuminaireHeight: MAX_HEIGHT });
    }
  };

  if (!isOpen) return null;

  return (
    <div
      ref={popupRef}
      className="fixed right-0 h-[calc(100dvh-64px)] w-[400px] flex flex-col border-l border-gray-700 shadow-2xl z-50"
      style={{ backgroundColor: greyP5 }}
    >
      {/* Header */}
      <div className="p-4 border-b border-gray-700 flex-shrink-0">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-bold text-white"> Auto Position Luminaires</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors p-1 hover:bg-gray-800 rounded-lg flex-shrink-0"
          >
            <X size={20} />
          </button>
        </div>
        <p className="text-gray-300 text-xs">
          Define which layers can hold luminaires. At least one layer must be marked as{' '}
          <span className="text-orange-500">"Can Hold Luminaire: Yes"</span> to enable automatic positioning.
        </p>
      </div>

      {/* Search */}
      <div className="p-2 border-b border-gray-700 flex-shrink-0">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            placeholder="Search layers..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 transition-all"
            style={{
              backgroundColor: greyP4,
            }}
          />
        </div>
      </div>

      {/* Warning Message */}
      {showWarning && (
        <div className="mx-4 mt-3 p-3 rounded-lg border border-red-500 bg-red-900 bg-opacity-20 flex-shrink-0">
          <div className="flex items-start">
            <X className="h-4 w-4 text-red-400 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-red-300 ml-2">At least one layer must have "Can Hold Luminaire" set to YES.</p>
            <button
              onClick={() => setShowWarning(false)}
              className="text-red-400 hover:text-red-300 ml-auto flex-shrink-0"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        </div>
      )}

      {/* Layer Table */}
      <div className="flex-1 overflow-hidden">
        <div className="h-full overflow-auto">
          <table className="w-full table-fixed">
            <thead className="sticky top-0 z-10" style={{ backgroundColor: greyP5 }}>
              <tr>
                <th className="w-[45%] text-left p-2 pl-4 text-gray-300 font-semibold text-xs border-b border-gray-700">
                  Layer Name
                </th>
                <th className="w-[25%] text-center p-2 text-gray-300 font-semibold text-xs border-b border-gray-700">
                  Can Hold Luminaire
                </th>
                <th className="w-[30%] text-center p-2 text-gray-300 font-semibold text-xs border-b border-gray-700">
                  Height (m) [2, 25]
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredLayers.map((layer, index) => (
                <tr
                  key={layer.uuid}
                  className="transition-colors"
                  style={{
                    backgroundColor: index % 2 === 0 ? greyP5 : '#1f1f1f',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = greyP4;
                    setHoveredLayer(layer.threeJsName);
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = index % 2 === 0 ? greyP5 : '#1f1f1f';
                    setHoveredLayer(null);
                  }}
                >
                  <td className="w-[45%] p-2 pl-4 text-white border-b border-gray-700 text-xs truncate">
                    <span title={layer.originalName}>{layer.originalName}</span>
                  </td>
                  <td className="w-[25%] p-2 text-center border-b border-gray-700">
                    <div className="flex justify-center">
                      <ToggleButton isOn={layer.canHoldLuminaire} onToggle={() => toggleCanHoldLuminaire(layer.uuid)} />
                    </div>
                  </td>
                  <td className="w-[30%] p-2 text-center border-b border-gray-700">
                    <div className="flex justify-center">
                      {layer.canHoldLuminaire ? (
                        <input
                          type="number"
                          min="2"
                          max="25"
                          step="0.01"
                          value={layer.desiredLuminaireHeight ?? desiredLuminaireHeight}
                          onChange={(e) => handleLayerHeightChange(layer.uuid, parseFloat(e.target.value))}
                          className="w-14 px-1 py-1 border border-gray-600 rounded text-white text-center text-xs focus:outline-none focus:ring-1 transition-all"
                          style={{
                            backgroundColor: greyP4,
                          }}
                        />
                      ) : (
                        <span className="text-gray-500 text-xs">N/A</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filteredLayers.length === 0 && (
            <div className="p-6 text-center text-gray-400 text-sm">No layers found matching "{searchTerm}"</div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="p-4 border-t border-gray-700 flex-shrink-0">
        <div className="flex flex-col gap-3">
          <div className="flex items-center">
            <label htmlFor="luminaireHeight" className="text-white mr-2 text-xs">
              Default Height (m) [2, 25]:
            </label>
            <input
              id="luminaireHeight"
              type="number"
              min="2"
              max="25"
              step="0.01"
              value={luminaireHeight}
              onChange={handleLuminaireHeightChange}
              className="w-16 px-2 py-1 border border-gray-600 rounded text-white placeholder-gray-400 focus:outline-none focus:ring-1 transition-all text-xs"
              style={{
                backgroundColor: greyP4,
              }}
            />
          </div>
          <button
            onClick={handleStartAutoPositioning}
            className="w-full text-black px-3 py-2 rounded-lg text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2"
            style={{
              backgroundColor: green01,
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = green03)}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = green01)}
          >
            Start Autopositioning
          </button>
        </div>
      </div>
    </div>
  );
};

export default AutoPositionSidebar;
